import React, { useContext, useMemo } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import socialData from '../data/javascripts/socialData';

const Contact = () => {
    const { darkMode } = useContext(ThemeContext);

    const themeClasses = useMemo(() => ({
        sectionBg: 'bg-bg-light dark:bg-bg-dark',
        headingText: 'text-primary dark:text-secondary',
        subText: 'text-text-light dark:text-text-dark',
        mutedText: 'text-muted-light dark:text-muted-dark',
        
        cardShadow: 'shadow-md hover:shadow-xl dark:shadow-none transition-shadow duration-300',
        
        iconBg: darkMode ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-100 hover:bg-gray-200',
    }), [darkMode]);

    const getIconColor = (name) => {
        switch (name) {
            case 'LinkedIn': return '#0A66C2';
            case 'GitHub': return darkMode ? '#f0f0f0' : '#333';
            case 'Twitter': return '#1DA1F2';
            case 'Instagram': return '#E1306C';
            case 'Facebook': return '#1877F2';
            case 'YouTube': return '#FF0000';
            default: return darkMode ? '#f0f0f0' : '#333';
        }
    };

    return (
        <section id="contact" className={`${themeClasses.sectionBg}`}>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                {/* --- Header Section --- */}
                <div className="text-center mb-12">
                    <h2 className={`text-3xl font-bold mb-4 ${themeClasses.headingText}`}>
                        Let's Collaborate
                    </h2>
                    <p className={`text-xl ${themeClasses.mutedText}`}>
                        I'm available on these platforms. Let's connect!
                    </p>
                </div>

                {/* --- Contact Card Layout (Image + Links) --- */}
                <div className="flex justify-center">
                    <div className="w-full max-w-5xl">
                        {/* FIX: Add rounded-xl to the parent div so overflow-hidden correctly clips the children's borders. */}
                        <div className={`flex flex-col lg:flex-row overflow-hidden rounded-xl ${themeClasses.cardShadow} border border-transparent dark:border-transparent`}>
                            
                            {/* 1. Image/Info Column (Left on desktop, Top on mobile) */}
                            <div 
                                className={`w-full lg:w-1/2 relative h-[350px] lg:h-auto flex flex-col justify-center items-center 
                                    lg:border-r border-border-light dark:border-border-dark 
                                    rounded-t-xl lg:rounded-tr-none lg:rounded-bl-xl
                                    `}
                            >
                                <img
                                    src='/images/connect.webp'
                                    alt="Connect with me"
                                    className="w-full h-full object-cover transition-transform duration-300 ease-in-out hover:scale-[1.02] 
                                        rounded-t-lg lg:rounded-tr-none lg:rounded-bl-lg"
                                />
                            </div>

                            {/* 2. Social Links Column (Right on desktop, Bottom on mobile) */}
                            <div 
                                className={`w-full lg:w-1/2 flex items-center p-6 md:p-10
                                    rounded-b-xl lg:rounded-bl-none lg:rounded-tr-xl
                                    `}
                            >
                                <div className="w-full">
                                    <div className="text-center mb-8">
                                        <h3 className={`text-2xl font-semibold mb-3 ${themeClasses.subText}`}>Find me across the web</h3>
                                        <p className={`${themeClasses.mutedText} text-md`}>
                                            To find my other works, please connect with me on the channels listed below.
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 justify-items-center">
                                        {socialData.map((item, index) => (
                                            <a
                                                key={index}
                                                href={item.url}
                                                className={`flex flex-col items-center text-center no-underline transition-transform duration-300 hover:scale-105 group ${themeClasses.subText}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={item.name}
                                            >
                                                <div
                                                    className={`w-16 h-16 rounded-full p-3 mb-2 flex items-center justify-center transition-all duration-300 ${themeClasses.iconBg} group-hover:shadow-lg`}
                                                >
                                                    <i
                                                        className={`bi bi-${item.icon} text-3xl`}
                                                        style={{ color: getIconColor(item.name) }}
                                                    ></i>
                                                </div>
                                                <span className={`text-sm font-medium ${themeClasses.subText}`}>
                                                    {item.name}
                                                </span>
                                            </a>
                                        ))}
                                    </div>

                                    <div className="text-center mt-8 pt-3 border-t border-border-light dark:border-border-dark">
                                        <p className={`text-sm ${themeClasses.mutedText}`}>
                                            I typically respond within 24 hours.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;